import { SubmitHandler } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { sendTestCampaignEmail, updateCampaign } from 'api/campaigns';
import * as Form from 'components/form';
import * as SettingsCard from 'components/settings-card';
import * as Wysiwyg from 'components/wysiwyg';
import { UseFormWysiwyg } from 'components/wysiwyg/UseFormWysiwyg';
import { CAMPAIGN_CUSTOM_RECEIPTS_LINK } from 'constants/links';
import { Settings } from 'constants/settings';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { GivebutterAxiosError } from 'types/error';

interface CampaignThankYouFormValues {
    message: string;
}

const CampaignThankYouSettings = () => {
    const toast = useToast();
    const { data: campaign } = useCurrentCampaign();
    const methods = Form.useGivebutterForm<CampaignThankYouFormValues>({
        defaultValues: {
            message: campaign?.thanksMessage?.value ?? '<p></p>',
        },
    });
    const { handleSubmit, control } = methods;

    const { mutate: submit } = useMutation({
        mutationFn: (values: CampaignThankYouFormValues) => {
            const data = {
                settings: [
                    {
                        code: Settings.CampaignSpecificThanksMessage.Code,
                        value: values.message,
                        active: values.message.length > 0,
                    },
                ],
            };

            return updateCampaign({ campaignId: campaign?.id!, data });
        },
        onSuccess: () => toast.success('Updated thank you message successfully!'),
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? 'Failed to update thank you message'),
    });

    const { mutate: sendTestEmail } = useMutation({
        mutationFn: () => sendTestCampaignEmail(campaign?.id!),
        onSuccess: () => toast.success('Successfully sent test email!'),
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? 'Failed to send test email'),
    });

    return (
        <Form.Provider {...methods}>
            <form onSubmit={handleSubmit(submit as SubmitHandler<CampaignThankYouFormValues>)}>
                <SettingsCard.Root>
                    <SettingsCard.LeftSide>
                        <SettingsCard.Heading>Thank you message</SettingsCard.Heading>
                        <p className={'text-sm text-gray-700'}>
                            Write a custom thank you message that will appear in email receipts.
                        </p>
                        <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href={CAMPAIGN_CUSTOM_RECEIPTS_LINK}
                            className={'text-sm'}
                        >
                            Read the guide
                        </a>
                        .
                    </SettingsCard.LeftSide>
                    <SettingsCard.RightSide>
                        <div className={'flex flex-col gap-4'}>
                            <Wysiwyg.Root>
                                <UseFormWysiwyg
                                    control={control}
                                    name="message"
                                    inline={false}
                                    init={{
                                        placeholder: 'Type your thank you message here...',
                                        menubar: false,
                                        statusbar: false,
                                        toolbar: 'bold italic link',
                                    }}
                                />
                            </Wysiwyg.Root>
                            <div className={'flex gap-4'}>
                                <Form.SubmitButton>Save</Form.SubmitButton>
                                <Button
                                    onClick={() => sendTestEmail()}
                                    variant={'outline'}
                                    color={'neutral'}
                                >
                                    Send yourself a test
                                </Button>
                            </div>
                        </div>
                    </SettingsCard.RightSide>
                </SettingsCard.Root>
            </form>
        </Form.Provider>
    );
};

export { CampaignThankYouSettings };
